import { DateHelper, type DateInput } from '#shared/format/date'

/**
 * Relative time ("how long ago"): the ONE module, with two styles (MAR-948).
 *
 * Both styles are genuinely needed:
 * - {@link formatRelativeTime}, the sentence style (`hace 3 h`, `ayer`, `5 min. ago`), for text that
 *   reads inline, such as "· hace 3 h" in the activity feed and the workflow run lists.
 * - {@link formatRelativeTimeCompact}, the column style (`5m`, `23h`, `2d`, `3mo`), for the
 *   fixed-width date columns of the document browser, where the sentence form does not fit.
 *
 * They used to be two unrelated copies (`lib/timeAgo.ts` and `DateHelper.formatTimeAgo`), each with
 * its own unit table, and the compact one hardcoded 'ahora'/'now'. Both now walk {@link UNITS}, and
 * every word either one prints comes from `Intl.RelativeTimeFormat` in the user's locale.
 * `tests/unit/formatHelpersSweep.test.ts` fails if a third one appears.
 */

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** Largest first. `suffix` is the compact style's unit; `null` means compact never uses it. */
const UNITS: ReadonlyArray<{ unit: Intl.RelativeTimeFormatUnit, ms: number, suffix: string | null }> = [
  { unit: 'year', ms: 365 * DAY, suffix: null },
  { unit: 'month', ms: 30 * DAY, suffix: 'mo' },
  { unit: 'day', ms: DAY, suffix: 'd' },
  { unit: 'hour', ms: HOUR, suffix: 'h' },
  { unit: 'minute', ms: MINUTE, suffix: 'm' },
]

function formatter(locale?: string | null) {
  return new Intl.RelativeTimeFormat(DateHelper.resolveLocale(locale), { numeric: 'auto', style: 'short' })
}

/** `hace 3 h`, `ayer`, `5 min. ago`. Empty for a missing or invalid date. */
export function formatRelativeTime(date: DateInput, locale?: string | null): string {
  const parsed = DateHelper.parse(date)
  if (!parsed) return ''

  const diffMs = parsed.getTime() - Date.now()
  const absMs = Math.abs(diffMs)
  const rtf = formatter(locale)

  for (let i = 0; i < UNITS.length; i++) {
    const { unit, ms } = UNITS[i]!
    if (absMs < ms) continue
    const value = Math.round(diffMs / ms)
    // Rounding can reach the next unit's boundary: 59.7 minutes selects MINUTE and rounds to 60,
    // printing "hace 60 min" where "hace 1 h" is meant. Go one unit coarser when it does (MAR-134).
    const coarser = UNITS[i - 1]
    if (coarser && Math.abs(value) * ms >= coarser.ms) {
      return rtf.format(Math.round(diffMs / coarser.ms), coarser.unit)
    }
    return rtf.format(value, unit)
  }
  return rtf.format(0, 'minute')
}

/**
 * `5m`, `23h`, `2d`, `3mo`: a bare number and a short unit, floored, for a narrow column. It stops at
 * months (`13mo`) rather than switching to years, so a column reads in one unit. Under a minute it
 * prints the locale's "now" (`ahora` / `now`). Empty for a missing or invalid date.
 */
export function formatRelativeTimeCompact(date: DateInput, locale?: string | null): string {
  const parsed = DateHelper.parse(date)
  if (!parsed) return ''

  const elapsed = Date.now() - parsed.getTime()
  for (const { ms, suffix } of UNITS) {
    if (suffix && elapsed >= ms) return `${Math.floor(elapsed / ms)}${suffix}`
  }
  return formatter(locale).format(0, 'second')
}
